/** Photographs attached to a day's reflection.
 *
 *  Two things here are not obvious and are the reason this is its own module.
 *
 *  **The token cannot ride on an `<img>`.** The API authenticates with a bearer
 *  header, and `<img src>` sends no headers — so a photo cannot simply be
 *  pointed at. Every image is fetched with the session token and turned into an
 *  object URL, which then has to be revoked or the blob leaks for the lifetime
 *  of the tab. `usePhotoUrl` owns that lifecycle.
 *
 *  **The resize is load-bearing, not a nicety.** A phone photograph is 3–5MB;
 *  a year of them at full size is over a gigabyte on a small droplet. Resized
 *  to a 1600px long edge it is roughly 300KB, so the route's 8MB limit becomes
 *  headroom rather than a wall. Re-encoding through a canvas also **drops EXIF**
 *  — including where the photograph was taken, which a journal that syncs to a
 *  server should not carry off the phone by accident.
 */

import { ApiError, request, type Session } from './client';
import { newIdempotencyKey } from './ids';

/** The long edge, in pixels, every upload is fitted inside. */
export const PHOTO_EDGE = 1600;
/** JPEG quality for the re-encode. 0.8 is where the size drop stops being free. */
export const PHOTO_QUALITY = 0.8;

export interface Photo {
  id: string;
  journal_date: string;
  content_type: string;
  byte_size: number;
  width: number;
  height: number;
}

/** The dimensions an image should be re-encoded at.
 *
 *  Only ever shrinks: enlarging a small photograph would cost bytes and add no
 *  detail. Rounded rather than truncated so a 1601px edge does not come back as
 *  1599 and look like an off-by-one.
 */
export function fitWithin(
  width: number,
  height: number,
  edge: number = PHOTO_EDGE,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= edge || longest === 0) return { width, height };
  const scale = edge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Re-encode a chosen file as a JPEG small enough to upload.
 *
 *  Goes through `createImageBitmap` rather than an `<img>` element because it
 *  decodes HEIC on Safari — which is what an iPhone actually produces — and
 *  reports its failure rather than firing an error event on a detached element.
 */
export async function resizeForUpload(file: Blob): Promise<Blob> {
  const bitmap = await createImageBitmap(file).catch(() => {
    // Chrome cannot decode HEIC. Saying which file and why beats "upload failed".
    throw new UnreadableImage();
  });
  try {
    const { width, height } = fitWithin(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new UnreadableImage();
    context.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', PHOTO_QUALITY),
    );
    if (!blob) throw new UnreadableImage();
    return blob;
  } finally {
    // Held until here so a throw above still releases the decoded frame.
    bitmap.close();
  }
}

export class UnreadableImage extends Error {
  override name = 'UnreadableImage';
  constructor() {
    super('That image could not be read on this device.');
  }
}

export async function listPhotos(session: Session, journalDate: string): Promise<Photo[]> {
  const { body } = await request<{ items: Photo[] }>(
    session,
    `/v1/days/${journalDate}/reflection/photos`,
  );
  return body.items ?? [];
}

/** Upload one photograph, creating the reflection it belongs to if need be.
 *
 *  A photograph belongs to a reflection, not to a date, so the API refuses one
 *  for a day that has no reflection. Rather than making someone write a sentence
 *  before they can keep a picture — which refuses the exact case this feature is
 *  for — an empty reflection is written first. An empty reflection is a
 *  legitimate entry on the days when the honest record *is* the photograph.
 */
export async function uploadPhoto(
  session: Session,
  journalDate: string,
  file: Blob,
): Promise<Photo> {
  const resized = await resizeForUpload(file);
  const send = () =>
    request<Photo>(session, `/v1/days/${journalDate}/reflection/photos`, {
      method: 'POST',
      body: resized,
      idempotencyKey: newIdempotencyKey(),
    });

  try {
    return (await send()).body;
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 404) throw error;
    await request(session, `/v1/days/${journalDate}/reflection`, {
      method: 'PUT',
      idempotencyKey: newIdempotencyKey(),
      body: { content: '', status: 'committed', provenance: 'typed' },
    });
    return (await send()).body;
  }
}

export async function deletePhoto(session: Session, id: string): Promise<void> {
  await request(session, `/v1/photos/${id}`, { method: 'DELETE' });
}

/** Fetch a photograph's bytes as an object URL.
 *
 *  The caller must revoke it. See `usePhotoUrl`, which does.
 */
export async function fetchPhotoUrl(session: Session, id: string): Promise<string> {
  const response = await fetch(`${session.baseUrl}/v1/photos/${id}`, {
    headers: { authorization: `Bearer ${session.token}` },
  });
  if (!response.ok) throw new ApiError(response.status, null, `HTTP ${response.status}`);
  return URL.createObjectURL(await response.blob());
}
